/**
 * Staged-file preview strip shown above the composer while attachments upload.
 * Image files render a thumbnail; everything else renders a typed chip. Each
 * preview has a remove (×) affordance and a spinner while uploading. Mirrors
 * chat.js's renderAttachmentPreviews.
 */
import { SpinnerIcon, XIcon } from '@phosphor-icons/react';
import type { PendingAttachment } from '@/features/chat/types';

function humanSize(bytes?: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

function typeLabel(type: string): string {
  if (type === 'application/pdf') return 'PDF';
  if (type.includes('spreadsheet') || type.includes('excel')) return 'XLS';
  if (type === 'text/csv') return 'CSV';
  if (type === 'text/plain') return 'TXT';
  return (type.split('/')[1] || 'FILE').toUpperCase().slice(0, 4);
}

type Props = {
  attachments: PendingAttachment[];
  onRemove: (clientId: string) => void;
};

export function AttachmentPreviews({ attachments, onRemove }: Props) {
  if (attachments.length === 0) return null;

  return (
    <div className="mx-auto flex max-w-3xl flex-wrap gap-2 px-1 pb-2">
      {attachments.map((att) => {
        const isImage = att.type.startsWith('image/');
        return (
          <div
            className="relative flex items-center gap-2 rounded-md border border-border-subtle bg-surface-foreground p-2"
            // data-testid="chat-attachment-preview" — e2e attachments spec asserts
            // a chosen file renders a preview chip (selectors.ts attachmentPreviews).
            data-testid="chat-attachment-preview"
            key={att.clientId}
          >
            {isImage && att.preview ? (
              <img
                alt={att.name}
                className="size-10 rounded object-cover"
                src={att.preview}
              />
            ) : (
              <span className="flex size-10 items-center justify-center rounded bg-surface-background font-inter font-semibold text-content-secondary text-xs">
                {typeLabel(att.type)}
              </span>
            )}
            <div className="flex flex-col">
              <span className="max-w-[10rem] truncate font-inter text-content-primary text-xs">
                {att.name}
              </span>
              <span className="font-inter text-content-secondary text-xs">
                {humanSize(att.size)}
              </span>
            </div>
            {att.uploading ? (
              <SpinnerIcon className="size-icon-sm animate-spin text-content-secondary" />
            ) : (
              <button
                aria-label={`Remove ${att.name}`}
                className="text-content-secondary hover:text-content-primary"
                onClick={() => onRemove(att.clientId)}
                type="button"
              >
                <XIcon className="size-icon-sm" />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
